import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { toastModule } from "@/modules/toast";
import { logger } from "@/modules/logger";
import type { Uuid } from "@/domains/global/identifiers";
import { useAppStore } from "@/store";

import { NoteSourceArtifactInfo } from "@/components/note/editor/source-bar/types";
import type { ArtifactResponse } from "@/components/note/editor/source-bar/types";
import type { NoteSourceAudioArtifactData } from "@/store/sources/types";
import {
  DEFAULT_RETRANSCRIBE_ERROR_MESSAGE,
  getRetranscribeErrorMessageFromUnknown,
  retranscribeVoiceSource,
} from "./retranscribe-voice-source";

type UseRetranscribeVoiceSourceArgs = {
  artifactInfo: NoteSourceArtifactInfo | null;
  sourceId?: string;
  downloadUrl?: string | null;
  audioRecordingId?: Uuid | null;
};

type UseRetranscribeVoiceSourceResult = {
  isRetranscribing: boolean;
  handleRetranscribe: () => Promise<void>;
};

const RETRANSCRIBE_UNAVAILABLE_MESSAGE = "Unable to retranscribe this voice source.";
const RETRANSCRIBE_SUCCESS_MESSAGE = "Transcript will refresh shortly.";
const RETRANSCRIBE_IMMEDIATE_SUCCESS_MESSAGE = "Transcript updated.";
const SUCCESS_DELAY_MS = 4000;

const buildTranscriptArray = (
  transcriptText: string
): NoteSourceAudioArtifactData["transcript_array"] => [
  {
    sort_key: "retranscribed-0",
    transcript: transcriptText,
    channel: 0,
  },
];

export const useRetranscribeVoiceSource = ({
  artifactInfo,
  sourceId,
  downloadUrl,
  audioRecordingId,
}: UseRetranscribeVoiceSourceArgs): UseRetranscribeVoiceSourceResult => {
  const { store } = useAppStore();
  const [isRetranscribing, setIsRetranscribing] = useState(false);
  const queryClient = useQueryClient();
  const audioRecordings = store.dexie.repositories.audioRecordings;

  const markRetranscriptionStatus = useCallback(
    async (status: "STARTED" | "COMPLETED" | "ERRORED") => {
      if (!audioRecordingId) return;

      try {
        if (status === "STARTED") {
          await audioRecordings.markAsRetranscribingStarted({ id: audioRecordingId });
          return;
        }

        if (status === "COMPLETED") {
          await audioRecordings.markAsRetranscribingCompleted({ id: audioRecordingId });
          return;
        }

        await audioRecordings.markAsRetranscribingErrored({ id: audioRecordingId });
      } catch (error) {
        logger.warn({
          message:
            "[useRetranscribeVoiceSource] Failed to update retranscription status for manual retranscribe",
          info: {
            audioRecordingId,
            status,
            error: String(error),
          },
        });
      }
    },
    [audioRecordingId, audioRecordings]
  );

  const applyTranscriptOverride = useCallback(
    (transcriptText: string) => {
      if (!sourceId) return;
      const normalizedTranscript = transcriptText.trim();
      if (!normalizedTranscript) return;

      queryClient.setQueryData<ArtifactResponse | undefined>(
        ["note-source-artifact-info", sourceId],
        previous => {
          if (!previous || previous.value?.artifact_kind !== "AUDIO") {
            return previous;
          }

          const audioValue = previous.value as NoteSourceAudioArtifactData;

          return {
            ...previous,
            value: {
              ...audioValue,
              transcript_text: normalizedTranscript,
              transcript_array: buildTranscriptArray(normalizedTranscript),
            },
          };
        }
      );
    },
    [queryClient, sourceId]
  );

  const handleRetranscribe = useCallback(async () => {
    if (artifactInfo?.kind !== "AUDIO") return;

    if (!downloadUrl) {
      toastModule.triggerToast({ content: RETRANSCRIBE_UNAVAILABLE_MESSAGE });
      return;
    }

    setIsRetranscribing(true);
    let shouldDelayOnSuccess = false;
    let shouldShowImmediateSuccess = false;
    let retranscriptionStatus: "COMPLETED" | "ERRORED" | null = null;

    try {
      await markRetranscriptionStatus("STARTED");

      const result = await retranscribeVoiceSource({ downloadUrl });

      if (!result.success) {
        retranscriptionStatus = "ERRORED";
        toastModule.triggerToast({ content: result.errorMessage });
      } else {
        retranscriptionStatus = "COMPLETED";
        if (result.transcriptContent) {
          applyTranscriptOverride(result.transcriptContent);
          shouldShowImmediateSuccess = true;
        } else {
          shouldDelayOnSuccess = true;
        }
      }
    } catch (error) {
      retranscriptionStatus = "ERRORED";
      const errorMessage =
        getRetranscribeErrorMessageFromUnknown(error) || DEFAULT_RETRANSCRIBE_ERROR_MESSAGE;
      toastModule.triggerToast({ content: errorMessage });
    } finally {
      if (retranscriptionStatus === "COMPLETED") {
        await markRetranscriptionStatus("COMPLETED");
      } else if (retranscriptionStatus === "ERRORED") {
        await markRetranscriptionStatus("ERRORED");
      }

      if (shouldShowImmediateSuccess) {
        toastModule.triggerToast({ content: RETRANSCRIBE_IMMEDIATE_SUCCESS_MESSAGE });
      }

      if (shouldDelayOnSuccess) {
        await new Promise(resolve => setTimeout(resolve, SUCCESS_DELAY_MS));
        toastModule.triggerToast({ content: RETRANSCRIBE_SUCCESS_MESSAGE });
      }

      setIsRetranscribing(false);
    }
  }, [artifactInfo, applyTranscriptOverride, downloadUrl, markRetranscriptionStatus]);

  return {
    isRetranscribing,
    handleRetranscribe,
  };
};
